import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconX,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import styles from './TutorialDialog.module.scss';

interface TutorialDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuideStep {
  title: string;
  desc: string;
  points: string[];
}

const TutorialDialog: React.FC<TutorialDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('dashboard');
  const [step, setStep] = useState(0);

  if (!isOpen) return null;

  const steps = t('tutorial.steps', { returnObjects: true }) as GuideStep[];
  const total = steps.length;
  const current = steps[step];
  const isLast = step === total - 1;

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    setStep(s => s - 1);
  };

  const handleClose = () => {
    onClose();
    setStep(0);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
          <IconX size={15} />
        </button>

        <div className={styles.stepContent}>
          <h2 className={styles.stepTitle}>{current.title}</h2>
          <p className={styles.stepDesc}>{current.desc}</p>
          <ul className={styles.pointList}>
            {current.points.map((point, i) => (
              <li key={i} className={styles.pointItem}>
                <span className={styles.pointDot} />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.dots}>
          {steps.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === step ? styles.dotActive : ''}`}
              onClick={() => setStep(i)}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.btnBack}
            onClick={handlePrev}
            disabled={step === 0}
          >
            <IconChevronLeft size={14} />
            {t('tutorial.prev')}
          </button>
          <span className={styles.stepCount}>
            {step + 1} / {total}
          </span>
          <button className={styles.btnNext} onClick={handleNext}>
            {isLast ? t('tutorial.done') : t('tutorial.next')}
            {!isLast && <IconChevronRight size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialDialog;
